import type { OnboardingStateResponse, OnboardingStepResponse } from '@/api/generated/model';

/**
 * «راه‌اندازی کلینیک» — the wizard's own map of the server's checklist.
 *
 * The server owns WHETHER a step is done (`GET /clinics/current/onboarding`,
 * derived from real rows every time). This file owns only how the wizard
 * walks those steps: their order, their short labels and their URL keys. The
 * order is deliberate and differs from the server's list in one place —
 * doctors come before services, because a starter kit builds a weekly roster
 * only for doctors who already exist (`starter-kit.service.ts`), so a kit
 * applied first would leave the hours step open with nothing explaining why.
 */

export type WizardStepKey = 'profile' | 'doctors' | 'services' | 'hours' | 'stock';
export type WizardScreen = WizardStepKey | 'done';

export interface WizardStep {
  key: WizardStepKey;
  /** The server checklist's `key` this screen completes. */
  serverKey: string;
  /**
   * The `panelPath` the server sends for that step today. The wizard stands in
   * for the step only while it still does: if the server moves a step (a
   * plan-aware stock step pointing at the subscription, say), the server's
   * path wins rather than a wizard screen the step no longer means.
   */
  serverPath: string;
  title: string;
  /** One line under the title, in the rail and the step header. */
  hint: string;
}

export const WIZARD_STEPS: readonly WizardStep[] = [
  {
    key: 'profile',
    serverKey: 'profile',
    serverPath: '/settings/clinic',
    title: 'مشخصات کلینیک',
    hint: 'نام، تلفن و نشانی',
  },
  {
    key: 'doctors',
    serverKey: 'staff',
    serverPath: '/staff',
    title: 'پزشکان',
    hint: 'چه کسانی نوبت می‌گیرند',
  },
  {
    key: 'services',
    serverKey: 'procedures',
    serverPath: '/procedures',
    title: 'خدمات و قیمت‌ها',
    hint: 'چه خدماتی ارائه می‌دید',
  },
  {
    key: 'hours',
    serverKey: 'schedule',
    serverPath: '/settings/schedule',
    title: 'ساعات کاری',
    hint: 'کلینیک چه روزهایی بازه',
  },
  {
    key: 'stock',
    serverKey: 'inventory',
    serverPath: '/inventory/receive',
    title: 'موجودی اولیه',
    hint: 'اختیاری',
  },
];

export const SETUP_PATH = '/setup';

/** `/setup?step=<key>` — the one href every surface uses for a wizard screen. */
export function setupHref(screen: WizardScreen): string {
  return `${SETUP_PATH}?step=${screen}`;
}

/**
 * The wizard screen that completes a server step, or `null` for one it does
 * not cover — or one the server has since pointed somewhere else.
 */
export function wizardKeyForServerStep(
  serverKey: string,
  panelPath?: string,
): WizardStepKey | null {
  const step = WIZARD_STEPS.find((candidate) => candidate.serverKey === serverKey);
  if (!step) return null;
  if (panelPath !== undefined && panelPath !== step.serverPath) return null;
  return step.key;
}

export function isWizardScreen(value: string | null): value is WizardScreen {
  return value === 'done' || WIZARD_STEPS.some((step) => step.key === value);
}

/** The server's step behind a wizard screen. */
export function serverStepOf(
  state: OnboardingStateResponse | undefined,
  key: WizardStepKey,
): OnboardingStepResponse | null {
  const serverKey = WIZARD_STEPS.find((step) => step.key === key)?.serverKey;
  return state?.steps.find((step) => step.key === serverKey) ?? null;
}

/**
 * The steps this clinic is shown: the stock step only when it is on the
 * server's checklist (it is, on every plan with simple stock) AND the plan
 * includes `inventory`.
 */
export function visibleSteps(
  state: OnboardingStateResponse | undefined,
  hasInventory: boolean,
): readonly WizardStep[] {
  return WIZARD_STEPS.filter(
    (step) =>
      step.key !== 'stock' ||
      (hasInventory && (!state || state.steps.some((s) => s.key === step.serverKey))),
  );
}

/**
 * Where an owner who opens `/setup` with no step should land: the first
 * REQUIRED screen still open, else «تمام شد».
 */
export function firstOpenScreen(
  state: OnboardingStateResponse | undefined,
  steps: readonly WizardStep[],
): WizardScreen {
  const open = steps.find((step) => {
    const server = serverStepOf(state, step.key);
    return server ? server.required && !server.done : false;
  });
  return open?.key ?? 'done';
}

/** The screen after `current` in this clinic's walk, ending at «تمام شد». */
export function nextScreen(current: WizardScreen, steps: readonly WizardStep[]): WizardScreen {
  if (current === 'done') return 'done';
  const index = steps.findIndex((step) => step.key === current);
  return steps[index + 1]?.key ?? 'done';
}

export function previousScreen(
  current: WizardScreen,
  steps: readonly WizardStep[],
): WizardStepKey | null {
  if (current === 'done') return steps[steps.length - 1]?.key ?? null;
  const index = steps.findIndex((step) => step.key === current);
  return index > 0 ? (steps[index - 1]?.key ?? null) : null;
}

/* -------------------------------------------------------------------------- */
/* Opening hours                                                              */
/* -------------------------------------------------------------------------- */

export type HoursWeekday =
  'SATURDAY' | 'SUNDAY' | 'MONDAY' | 'TUESDAY' | 'WEDNESDAY' | 'THURSDAY' | 'FRIDAY';

export interface DayHours {
  weekday: HoursWeekday;
  open: boolean;
  /** Minutes from midnight. */
  start: number;
  end: number;
}

/** شنبه تا چهارشنبه ۱۰ تا ۲۰، پنجشنبه ۱۰ تا ۱۴، جمعه تعطیل — the usual clinic week. */
export const DEFAULT_WEEK: readonly DayHours[] = [
  { weekday: 'SATURDAY', open: true, start: 600, end: 1200 },
  { weekday: 'SUNDAY', open: true, start: 600, end: 1200 },
  { weekday: 'MONDAY', open: true, start: 600, end: 1200 },
  { weekday: 'TUESDAY', open: true, start: 600, end: 1200 },
  { weekday: 'WEDNESDAY', open: true, start: 600, end: 1200 },
  { weekday: 'THURSDAY', open: true, start: 600, end: 840 },
  { weekday: 'FRIDAY', open: false, start: 600, end: 840 },
];

/** «۱۰:۰۰» style 24-hour wall clock for the clinic's public hours (ASCII, the DTO's format). */
export function minuteToHhmm(minute: number): string {
  const hours = Math.floor(minute / 60);
  const minutes = minute % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

export function hhmmToMinute(value: string | null | undefined): number | null {
  const match = /^(\d{1,2}):(\d{2})$/.exec(value ?? '');
  if (!match) return null;
  const minute = Number(match[1]) * 60 + Number(match[2]);
  return minute >= 0 && minute <= 24 * 60 ? minute : null;
}

/** The first day whose hours cannot be saved, as «[why]. [what to do].», or `null`. */
export function weekBlocker(week: readonly DayHours[]): string | null {
  if (!week.some((day) => day.open)) {
    return 'هیچ روزی باز نیست. حداقل یک روز کاری رو باز کنید.';
  }
  if (week.some((day) => day.open && day.end <= day.start)) {
    return 'در یکی از روزها ساعت پایان قبل از شروع است. ساعت پایان رو دیرتر بذارید.';
  }
  return null;
}

/**
 * The week as the bulk roster endpoint wants it: one call per distinct
 * opening pattern (`replaceDays`), plus one `clearDays` for the closed days.
 * Grouping keeps it to two or three requests for a typical week instead of
 * seven, and each is all-or-nothing on the server.
 */
export function rosterCalls(
  week: readonly DayHours[],
): Array<
  | { mode: 'replaceDays'; weekdays: HoursWeekday[]; startMinute: number; endMinute: number }
  | { mode: 'clearDays'; weekdays: HoursWeekday[] }
> {
  const patterns = new Map<string, { start: number; end: number; days: HoursWeekday[] }>();
  for (const day of week) {
    if (!day.open) continue;
    const key = `${day.start}-${day.end}`;
    const entry = patterns.get(key) ?? { start: day.start, end: day.end, days: [] };
    entry.days.push(day.weekday);
    patterns.set(key, entry);
  }
  const closed = week.filter((day) => !day.open).map((day) => day.weekday);
  return [
    ...[...patterns.values()].map((pattern) => ({
      mode: 'replaceDays' as const,
      weekdays: pattern.days,
      startMinute: pattern.start,
      endMinute: pattern.end,
    })),
    ...(closed.length > 0 ? [{ mode: 'clearDays' as const, weekdays: closed }] : []),
  ];
}

/** The clinic's public opening hours (`UpdateClinicDto.workingHours`) for the same week. */
export function publicHours(
  week: readonly DayHours[],
): Array<{ weekday: HoursWeekday; closed: boolean; open?: string; close?: string }> {
  return week.map((day) =>
    day.open
      ? {
          weekday: day.weekday,
          closed: false,
          open: minuteToHhmm(day.start),
          close: minuteToHhmm(day.end),
        }
      : { weekday: day.weekday, closed: true },
  );
}

/** The week stored on the clinic, or the usual week when it has none yet. */
export function weekFromClinic(
  stored: ReadonlyArray<{
    weekday: string;
    closed: boolean;
    open?: string | null;
    close?: string | null;
  }>,
): DayHours[] {
  if (stored.length === 0) return DEFAULT_WEEK.map((day) => ({ ...day }));
  return DEFAULT_WEEK.map((fallback) => {
    const row = stored.find((entry) => entry.weekday === fallback.weekday);
    if (!row) return { ...fallback, open: false };
    const start = hhmmToMinute(row.open);
    const end = hhmmToMinute(row.close);
    return {
      weekday: fallback.weekday,
      open: !row.closed && start !== null && end !== null,
      start: start ?? fallback.start,
      end: end ?? fallback.end,
    };
  });
}
